import { Consensus } from '@cadstrom/cadstrom-shared';
import { Markdown } from '@cadstrom/sigma-engine-shared';
import { skipToken, useQuery } from '@tanstack/react-query';

import { ClauseChip } from '#components/clause-chip/clause-chip.js';
import { UnresolvedClauseChip } from '#components/clause-chip/unresolved-clause-chip.js';
import { useTRPC } from '#src/trpc.js';

export interface ClauseMarkdownItemProps {
	label?: string;
}

/**
 * Renders a markdown clause reference such as `:clause[floating_behaviour #6]` as a clause chip, whose
 * tooltip shows the clause's short name, text, version, and author. A reference without a version shows the
 * current wording. A reference with a version shows that version, or the current wording if that version
 * doesn't exist. While the clause loads, the label appears in a plain chip. If the clause can't be found
 * (bad format, unknown property or number, or no permission to view the specification), the plain chip gets
 * a dashed border.
 *
 * @param props - {@link ClauseMarkdownItemProps}.
 * @returns The chip, or nothing when the reference has no label.
 */
export function ClauseMarkdownItem({ label }: ClauseMarkdownItemProps) {
	const trpc = useTRPC();
	const reference = label === undefined ? undefined : Markdown.parseClauseReferenceLabel(label);
	const propertyKey = Consensus.PropertyKeySchema.safeParse(reference?.propertyKey);
	const lineageInput =
		reference === undefined || !propertyKey.success ?
			undefined
		:	{ propertyKey: propertyKey.data, clauseNumber: reference.clauseNumber };
	const { data, isPending } = useQuery({
		...trpc.packageDataSpec.clause.getLineage.queryOptions(lineageInput ?? skipToken),
		throwOnError: false,
	});

	if (label === undefined) {
		return null;
	}

	const versions = data?.lineage.versions ?? [];
	const clause =
		versions.find((version) => version.version === reference?.version) ??
		versions.find((version) => version.isCurrentVersion);

	return (
		<span className='isolate mx-0.5 inline-flex items-center align-middle'>
			{clause === undefined ?
				<UnresolvedClauseChip label={label} failed={lineageInput === undefined || !isPending} />
			:	<ClauseChip clause={clause} qualified showShortName={false} />}
		</span>
	);
}
